import { useEffect, useState } from "react";
import { ShieldCheck, Save } from "lucide-react";
import { toast } from "sonner";
import { API, formatApiError } from "../lib/api";
import { ROLE_BADGE_COLORS } from "../lib/constants";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Skeleton } from "@/components/ui/skeleton";

export default function Roles() {
  const [roles, setRoles] = useState(null);
  const [permissions, setPermissions] = useState([]);
  const [edited, setEdited] = useState({});
  const [saving, setSaving] = useState("");

  const load = () => {
    API.get("/admin/roles")
      .then((r) => {
        setRoles(r.data.roles);
        setPermissions(r.data.permissions);
        const map = {};
        r.data.roles.forEach((role) => { map[role.code] = role.permissions; });
        setEdited(map);
      })
      .catch((e) => toast.error(formatApiError(e)));
  };

  useEffect(() => { load(); }, []);

  const toggle = (code, perm) => {
    setEdited((prev) => {
      const cur = new Set(prev[code] || []);
      if (cur.has(perm)) cur.delete(perm); else cur.add(perm);
      return { ...prev, [code]: Array.from(cur) };
    });
  };

  const save = async (code) => {
    setSaving(code);
    try {
      await API.put(`/admin/roles/${code}`, { permissions: edited[code] });
      toast.success("Role mis a jour.");
      load();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setSaving("");
    }
  };

  if (!roles) return <div className="space-y-4">{[...Array(3)].map((_, i) => <Skeleton key={i} className="h-40 w-full" />)}</div>;

  return (
    <div className="space-y-6" data-testid="roles-page">
      <div>
        <h1 className="text-2xl sm:text-3xl font-heading font-bold flex items-center gap-2">
          <ShieldCheck className="h-7 w-7 text-[#009E60]" /> Securite — Roles et permissions
        </h1>
        <p className="text-sm text-muted-foreground">Controle d'acces base sur les roles (RBAC). Les autorisations sont verifiees cote serveur.</p>
      </div>

      <div className="space-y-6">
        {roles.map((role) => {
          const isSuper = role.code === "SUPER_ADMINISTRATEUR";
          const current = edited[role.code] || [];
          return (
            <Card key={role.code} data-testid={`role-card-${role.code}`}>
              <CardHeader>
                <div className="flex flex-wrap items-center justify-between gap-2">
                  <div>
                    <CardTitle className="text-base font-heading flex items-center gap-2">
                      <Badge className={ROLE_BADGE_COLORS[role.code] || ""}>{role.name}</Badge>
                      <span className="text-xs text-muted-foreground font-normal">{role.user_count} utilisateur(s)</span>
                    </CardTitle>
                    <p className="mt-1 text-xs text-muted-foreground">{role.description}</p>
                  </div>
                  {!isSuper && (
                    <Button size="sm" onClick={() => save(role.code)} disabled={saving === role.code}
                      className="bg-[#009E60] hover:bg-[#00804e] text-white" data-testid={`role-save-${role.code}`}>
                      <Save className="h-4 w-4 mr-1" /> {saving === role.code ? "Enregistrement..." : "Enregistrer"}
                    </Button>
                  )}
                </div>
              </CardHeader>
              <CardContent>
                {isSuper ? (
                  <p className="text-sm text-muted-foreground italic">Toutes les permissions (role systeme non modifiable).</p>
                ) : (
                  <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-2">
                    {permissions.map((p) => (
                      <label key={p.key} className="flex items-center gap-2 rounded-lg border px-3 py-2 text-sm cursor-pointer hover:bg-accent transition-colors"
                        data-testid={`role-${role.code}-perm-${p.key}`}>
                        <Checkbox
                          checked={current.includes(p.key)}
                          onCheckedChange={() => toggle(role.code, p.key)}
                        />
                        <span>{p.label}</span>
                      </label>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          );
        })}
      </div>
    </div>
  );
}
